// src/pages/InboxDetail.tsx
import React from 'react'
import { DateTime } from 'luxon'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, atlas, enabledStacks } from '../api'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import { FileText, DollarSign, CheckCircle2, Star } from 'lucide-react'
import Spinner from '../components/Spinner'
import PaymentCard from '../components/PaymentCard'
import { ProfessionKey, PROFESSIONS, resolveProfessionParam } from '../professions'
import { RatingStarsDisplay, RatingStarsInput } from '../components/RatingStars'
import {
  DEFAULT_TIME_ZONE,
  detectTimeZone,
  formatCurrencyAmount,
  getCountryLabel,
  getCurrencyForCountry,
  getStripeFeeRuleForCountry,
  getTaxRuleForCountry,
  normalizeCountryCode,
} from '../countryRules'

const ENABLED_PROFESSIONS = (enabledStacks.length
  ? (enabledStacks as ProfessionKey[])
  : ['doctor']) as ProfessionKey[]

type PatientSummary = {
  ageYears?: number | null
  gender?: string | null
  state?: string | null
} | null

type PatientContact = {
  name?: string | null
  email?: string | null
  phone?: string | null
  state?: string | null
  postalCode?: string | null
} | null

type IncomingItem = {
  selectionId: string
  specialistId: string
  status: 'PENDING' | 'ACCEPTED' | 'CONFIRMED' | 'COMPLETED' | 'DECLINED' | 'LOST' | 'CANCELLED'
  slotISO: string
  createdAt: string
  feeCapCents: number | null
  specialty: string
  reason: string
  patientSummary?: PatientSummary
  patientId?: string
  patientName?: string | null
  patientContact?: PatientContact
  selectionCreatedAt?: string | null
  meeting?: {
    provider?: string | null
    joinUrl?: string | null
    hostUrl?: string | null
    meetingId?: string | null
    createdAt?: string | null
    sentAt?: string | null
    meetingTimeISO?: string | null
    meetingTimeZone?: string | null
    status?: string | null
  } | null
  review?: {
    client?: {
      submitted?: boolean
    }
  } | null
  invoiceStatus?: string | null
  paymentStatus?: string | null
  reviewReceived?: {
    submitted?: boolean
    rating?: number | null
    comment?: string | null
    createdAt?: string | null
    reviewerFirstName?: string | null
    providerReply?: {
      comment?: string | null
      createdAt?: string | null
      updatedAt?: string | null
      replierName?: string | null
    } | null
  } | null
}
const CONTACT_SLOT = 'REQUEST_BOOKING'
const isValidTimeZone = (value: string = '') => {
  if (!value) return false
  if (value.length > 64) return false
  const upper = value.toUpperCase()
  if (upper === 'UTC' || upper === 'GMT') return true
  if (!/^[A-Za-z0-9_+-]+(\/[A-Za-z0-9_+-]+)+$/.test(value)) return false
  try {
    new Intl.DateTimeFormat('en-AU', { timeZone: value })
    return true
  } catch {
    return false
  }
}
const normalizeAbn = (value: string = '') => value.replace(/\D/g, '')
const isValidAbn = (value: string = '') => {
  const abn = normalizeAbn(value)
  if (!/^\d{11}$/.test(abn)) return false
  const digits = abn.split('').map((digit) => Number(digit))
  digits[0] -= 1
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19]
  const sum = digits.reduce((acc, digit, idx) => acc + digit * weights[idx], 0)
  return sum % 89 === 0
}

type StripeStatus = {
  connected: boolean
  accountId?: string | null
  accountType?: string | null
  chargesEnabled?: boolean
  payoutsEnabled?: boolean
  detailsSubmitted?: boolean
  hasAccessToken?: boolean
  requirements?: {
    currentlyDue?: string[]
    pendingVerification?: string[]
    disabledReason?: string | null
  }
}
type ZoomStatus = {
  connected: boolean
  email?: string | null
  name?: string | null
  autoCreateMeetings?: boolean
}
type GoogleCalendarStatus = {
  connected: boolean
  email?: string | null
  reason?: string | null
  selectedCalendarIds?: string[]
  writeCalendarId?: string | null
  autoCreateMeetings?: boolean
}

type InvoiceRecord = {
  invoiceId: string
  status?: string | null
  amountCents?: number | null
  currency?: string | null
  hostedInvoiceUrl?: string | null
  pdfUrl?: string | null
  sentAt?: string | null
  paidAt?: string | null
  voidedAt?: string | null
  dueDate?: string | null
  memo?: string | null
  description?: string | null
  lineItems?: Array<{ description?: string | null; amountCents?: number | null }> | null
  includeGst?: boolean | null
  taxRateId?: string | null
  createdAt?: string | null
}

type InboxDetailContentProps = {
  selectionId: string
  initialStack?: ProfessionKey | null
  inline?: boolean
  onClose?: () => void
  onOpenNotes?: (selectionId: string) => void
  focusPayment?: boolean
  mode?: 'full' | 'payment' | 'meeting'
  onOpenPayment?: (selectionId: string) => void
  onOpenDetails?: (selectionId: string) => void
  onOpenMeeting?: (selectionId: string) => void
  onOpenReview?: (selectionId: string) => void
  onOpenReviewReminder?: (selectionId: string) => void
}

export function InboxDetailContent({
  selectionId,
  initialStack,
  inline = false,
  onClose,
  onOpenNotes,
  focusPayment = false,
  mode = 'full',
  onOpenPayment,
  onOpenDetails,
  onOpenMeeting,
  onOpenReview,
  onOpenReviewReminder,
}: InboxDetailContentProps) {
  const [searchParams] = useSearchParams()
  const paymentOnly = mode === 'payment'
  const meetingOnly = mode === 'meeting'
  const summaryOnly = paymentOnly || meetingOnly
  const preferredStack =
    initialStack && ENABLED_PROFESSIONS.includes(initialStack)
      ? initialStack
      : null
  const professionOrder = React.useMemo(() => {
    const keys = ENABLED_PROFESSIONS
    if (preferredStack && keys.includes(preferredStack)) {
      return [preferredStack, ...keys.filter((k) => k !== preferredStack)]
    }
    return keys
  }, [preferredStack])
  const [currentProfession, setCurrentProfession] = React.useState<ProfessionKey>(
    preferredStack || 'doctor'
  )
  const nav = useNavigate()
  const exitToInbox = React.useCallback(() => {
    if (inline && onClose) {
      onClose()
      return
    }
    nav(`/dashboard?stack=${currentProfession}`)
  }, [inline, onClose, nav, currentProfession])

  const [data, setData] = React.useState<IncomingItem | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [err, setErr] = React.useState<string | null>(null)

  const [needCard, setNeedCard] = React.useState(false)
  const [waiverConfirmOpen, setWaiverConfirmOpen] = React.useState(false)
  const [cardName, setCardName] = React.useState('')
  const [cardBusy, setCardBusy] = React.useState(false)
  const [cardErr, setCardErr] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [clientReviewDraft, setClientReviewDraft] = React.useState({ rating: 0 })
  const [reviewBusy, setReviewBusy] = React.useState(false)
  const [reviewError, setReviewError] = React.useState<string | null>(null)
  const [reviewNotice, setReviewNotice] = React.useState<string | null>(null)
  const [providerReplyDraft, setProviderReplyDraft] = React.useState('')
  const [replyBusy, setReplyBusy] = React.useState(false)
  const [replyError, setReplyError] = React.useState<string | null>(null)
  const [replyNotice, setReplyNotice] = React.useState<string | null>(null)
  const acceptancePerk = { reason: 'free', label: 'Acceptance is free.' }
  const [clientRatingSummary, setClientRatingSummary] = React.useState<{ ratingAverage?: number; ratingCount?: number } | null>(null)
  const [clientRatingReady, setClientRatingReady] = React.useState(false)
  const [flash, setFlash] = React.useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const flashTimerRef = React.useRef<number | null>(null)
  const paymentSectionRef = React.useRef<HTMLDivElement | null>(null)
  const reviewSectionRef = React.useRef<HTMLDivElement | null>(null)
  const reviewReplyInputRef = React.useRef<HTMLTextAreaElement | null>(null)
  const didAutoScrollRef = React.useRef(false)
  const didAutoScrollReviewRef = React.useRef(false)
  const [stripeStatus, setStripeStatus] = React.useState<StripeStatus | null>(null)
  const [stripeStatusLoading, setStripeStatusLoading] = React.useState(false)
  const [stripeStatusError, setStripeStatusError] = React.useState<string | null>(null)
  const [stripeDisconnecting, setStripeDisconnecting] = React.useState(false)
  const [zoomStatus, setZoomStatus] = React.useState<ZoomStatus | null>(null)
  const [zoomLoading, setZoomLoading] = React.useState(false)
  const [zoomError, setZoomError] = React.useState<string | null>(null)
  const [zoomAutoCreate, setZoomAutoCreate] = React.useState(false)
  const [zoomSaving, setZoomSaving] = React.useState(false)
  const [zoomDisconnecting, setZoomDisconnecting] = React.useState(false)
  const [googleStatus, setGoogleStatus] = React.useState<GoogleCalendarStatus | null>(null)
  const [googleLoading, setGoogleLoading] = React.useState(false)
  const [googleError, setGoogleError] = React.useState<string | null>(null)
  const [googleAutoCreate, setGoogleAutoCreate] = React.useState(false)
  const [googleSaving, setGoogleSaving] = React.useState(false)
  const [googleDisconnecting, setGoogleDisconnecting] = React.useState(false)
  const [meetingProvider, setMeetingProvider] = React.useState<'zoom' | 'google'>('zoom')
  const meetingProviderTouched = React.useRef(false)
  const [meetingBusy, setMeetingBusy] = React.useState(false)
  const [meetingError, setMeetingError] = React.useState<string | null>(null)
  const [meetingNotice, setMeetingNotice] = React.useState<string | null>(null)
  const [meetingTimeInput, setMeetingTimeInput] = React.useState('')
  const [meetingTimeConfirmed, setMeetingTimeConfirmed] = React.useState(false)
  const [meetingTimeDirty, setMeetingTimeDirty] = React.useState(false)
  const [meetingTimeZone, setMeetingTimeZone] = React.useState<string>(() => detectTimeZone())
  const [meetingTimeInputZone, setMeetingTimeInputZone] = React.useState<string>(() => detectTimeZone())
  const meetingPickerRef = React.useRef<HTMLInputElement | null>(null)
  const [invoiceList, setInvoiceList] = React.useState<InvoiceRecord[]>([])
  const [invoiceLoading, setInvoiceLoading] = React.useState(false)
  const [invoiceError, setInvoiceError] = React.useState<string | null>(null)
  const [invoiceBusy, setInvoiceBusy] = React.useState(false)
  const [invoiceNotice, setInvoiceNotice] = React.useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const invoiceNoticeTimerRef = React.useRef<number | null>(null)
  const [invoiceSentAt, setInvoiceSentAt] = React.useState<number | null>(null)
  const [invoiceDraft, setInvoiceDraft] = React.useState({
    amount: '',
    description: '',
    memo: '',
    includeGst: false,
    abn: '',
    saveAbn: true,
    passStripeFee: false,
  })
  const [invoiceProviderCountryCode, setInvoiceProviderCountryCode] = React.useState<string | null>(null)
  const [invoiceProviderRegionCode, setInvoiceProviderRegionCode] = React.useState<string | null>(null)
  const slotStartMs = data?.slotISO ? Date.parse(data.slotISO) : Number.NaN
  const slotElapsed = data?.slotISO === CONTACT_SLOT ? false : (Number.isFinite(slotStartMs) ? slotStartMs <= Date.now() : false)
  const focusReview = React.useMemo(() => {
    const reviewRaw = String(searchParams.get('review') || '').trim()
    const focusRaw = String(searchParams.get('focus') || '').trim().toLowerCase()
    if (focusRaw === 'review') return true
    if (!reviewRaw) return false
    const normalized = reviewRaw.toLowerCase()
    if (normalized === '1' || normalized === 'true' || normalized === 'review') return true
    return Boolean(selectionId && reviewRaw === selectionId)
  }, [searchParams, selectionId])
  const meetingQueryProviderHint = React.useMemo<'zoom' | 'google' | null>(() => {
    const qMeeting = (searchParams.get('meeting') || '').trim()
    if (!qMeeting || qMeeting !== selectionId) return null
    const provider = (searchParams.get('meetingProvider') || '').trim().toLowerCase()
    if (provider === 'zoom' || provider === 'google') return provider
    if (searchParams.get('zoom')) return 'zoom'
    if (searchParams.get('google')) return 'google'
    return null
  }, [searchParams, selectionId])
  const statusMeta = React.useMemo(() => {
    if (!data) return { label: '—', className: 'status-pending' }
    if (slotElapsed && data.status === 'PENDING') {
      return { label: 'Expired', className: 'status-lost' }
    }
    const status = (data.status || 'PENDING').toUpperCase()
    if (status === 'ACCEPTED' || status === 'CONFIRMED') {
      return { label: 'Accepted', className: 'status-accepted' }
    }
    if (status === 'DECLINED' || status === 'LOST' || status === 'CANCELLED') {
      return { label: 'Closed', className: 'status-lost' }
    }
    return { label: 'Pending', className: 'status-pending' }
  }, [data, slotElapsed])

  const stripe = useStripe()
  const elements = useElements()

  React.useEffect(() => {
    return () => {
      if (flashTimerRef.current) {
        window.clearTimeout(flashTimerRef.current)
        flashTimerRef.current = null
      }
      if (invoiceNoticeTimerRef.current) {
        window.clearTimeout(invoiceNoticeTimerRef.current)
        invoiceNoticeTimerRef.current = null
      }
    }
  }, [])
  React.useEffect(() => {
    setClientReviewDraft({ rating: 0 })
    setReviewError(null)
    setReviewNotice(null)
    setProviderReplyDraft('')
    setReplyError(null)
    setReplyNotice(null)
  }, [selectionId])
  React.useEffect(() => {
    meetingProviderTouched.current = false
  }, [selectionId])

  React.useEffect(() => {
    didAutoScrollRef.current = false
    didAutoScrollReviewRef.current = false
  }, [selectionId])

  React.useEffect(() => {
    setProviderReplyDraft(String(data?.reviewReceived?.providerReply?.comment || ''))
    setReplyError(null)
    setReplyNotice(null)
  }, [selectionId, data?.reviewReceived?.providerReply?.comment])

  React.useEffect(() => {
    setInvoiceError(null)
    setInvoiceList([])
    setStripeStatus(null)
    setStripeStatusError(null)
    setInvoiceNotice(null)
    setInvoiceSentAt(null)
  }, [selectionId])

  React.useEffect(() => {
    if (!invoiceSentAt) return
    const timer = window.setTimeout(() => setInvoiceSentAt(null), 4000)
    return () => window.clearTimeout(timer)
  }, [invoiceSentAt])

  const scrollToPayment = React.useCallback(() => {
    if (paymentSectionRef.current) {
      paymentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  React.useEffect(() => {
    if (!focusPayment || didAutoScrollRef.current === true) return
    if (!data || data.status !== 'ACCEPTED') return
    if (!paymentSectionRef.current) return
    didAutoScrollRef.current = true
    window.setTimeout(() => scrollToPayment(), 50)
  }, [focusPayment, data?.status, scrollToPayment, data])

  React.useEffect(() => {
    if (!focusReview || didAutoScrollReviewRef.current === true) return
    if (!data?.reviewReceived?.submitted) return
    if (!reviewSectionRef.current) return
    didAutoScrollReviewRef.current = true
    window.setTimeout(() => {
      reviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      reviewReplyInputRef.current?.focus()
    }, 50)
  }, [focusReview, data?.reviewReceived?.submitted, data?.selectionId])

  const load = React.useCallback(async () => {
    if (!selectionId) { setErr('Missing selectionId'); setLoading(false); return }
    setLoading(true); setErr(null)
    try {
      let found: IncomingItem | null = null
      for (const stack of professionOrder) {
        try {
          let cursor: string | undefined = undefined
          let pages = 0
          while (true) {
            const res = await atlas.getIncomingSelections(stack, { includeAll: true, cursor })
            const items: IncomingItem[] = res?.items || []
            const match = items.find(i => i.selectionId === selectionId) || null
            if (match) {
              found = match
              setCurrentProfession(stack)
              break
            }
            cursor = res?.nextCursor || undefined
            pages += 1
            if (!cursor || pages >= 12) break
          }
          if (found) break
        } catch (err: any) {
          const msg = String(err?.message || '')
          if (!msg.toLowerCase().includes('forbidden')) {
            throw err
          }
        }
      }

      if (!found) {
        setErr('This request is no longer pending for you.')
        setData(null)
      } else {
        setData(found)
      }
    } catch (e:any) {
      setErr(e?.message || 'Failed to load request')
    } finally {
      setLoading(false)
    }
  }, [selectionId, professionOrder])

  const loadStripeStatus = React.useCallback(async () => {
    setStripeStatusLoading(true)
    setStripeStatusError(null)
    try {
      const res = await api.stripeStatus()
      setStripeStatus(res || null)
    } catch (err: any) {
      setStripeStatusError(err?.message || 'Failed to load Stripe status')
    } finally {
      setStripeStatusLoading(false)
    }
  }, [])

  const loadZoomStatus = React.useCallback(async () => {
    setZoomLoading(true)
    setZoomError(null)
    try {
      const res = await api.zoomStatus(currentProfession)
      setZoomStatus(res || null)
      setZoomAutoCreate(Boolean(res?.autoCreateMeetings))
    } catch (err: any) {
      setZoomError(err?.message || 'Failed to load Zoom status')
    } finally {
      setZoomLoading(false)
    }
  }, [currentProfession])

  const loadGoogleStatus = React.useCallback(async () => {
    setGoogleLoading(true)
    setGoogleError(null)
    try {
      const res = await api.googleCalendarStatus(currentProfession)
      setGoogleStatus(res || null)
      setGoogleAutoCreate(Boolean(res?.autoCreateMeetings))
    } catch (err: any) {
      setGoogleError(err?.message || 'Failed to load Google Calendar status')
    } finally {
      setGoogleLoading(false)
    }
  }, [currentProfession])

  const loadCalendarSettings = React.useCallback(async () => {
    try {
      const res = await api.calendarSettings(currentProfession)
      const tz = typeof res?.timeZone === 'string' && res.timeZone.trim()
        ? res.timeZone.trim()
        : detectTimeZone()
      const safeTz = isValidTimeZone(tz) ? tz : DEFAULT_TIME_ZONE
      setMeetingTimeZone(safeTz)
    } catch (_err) {
      const fallback = detectTimeZone()
      setMeetingTimeZone(isValidTimeZone(fallback) ? fallback : DEFAULT_TIME_ZONE)
    }
  }, [currentProfession])

  const loadInvoices = React.useCallback(async () => {
    if (!selectionId) return
    setInvoiceLoading(true)
    setInvoiceError(null)
    try {
      const res = await atlas.listSelectionInvoices(currentProfession, selectionId)
      setInvoiceList(Array.isArray(res?.invoices) ? res.invoices : [])
    } catch (err: any) {
      setInvoiceError(err?.message || 'Failed to load invoices')
    } finally {
      setInvoiceLoading(false)
    }
  }, [currentProfession, selectionId])

  const showInvoiceNotice = React.useCallback((type: 'success' | 'error', message: string, ttlMs = 8000) => {
    setInvoiceNotice({ type, message })
    if (invoiceNoticeTimerRef.current) {
      window.clearTimeout(invoiceNoticeTimerRef.current)
      invoiceNoticeTimerRef.current = null
    }
    invoiceNoticeTimerRef.current = window.setTimeout(() => {
      setInvoiceNotice(null)
    }, ttlMs)
  }, [])

  React.useEffect(() => {
    let cancelled = false
    if (flashTimerRef.current) {
      window.clearTimeout(flashTimerRef.current)
      flashTimerRef.current = null
    }
    if (!data?.patientId) {
      setClientRatingSummary(null)
      setClientRatingReady(true)
      return
    }
    setClientRatingReady(false)
    ;(async () => {
      try {
        const summary = await api.getClientRatingSummary(data.patientId!, currentProfession)
        if (!cancelled) {
          setClientRatingSummary(summary || null)
          setClientRatingReady(true)
        }
      } catch (err) {
        if (!cancelled) {
          setClientRatingSummary(null)
          setClientRatingReady(true)
        }
      }
    })()
    return () => { cancelled = true }
  }, [data?.patientId, currentProfession])

  React.useEffect(() => {
    if (meetingOnly) return
    if (!data) return
    loadStripeStatus()
    const status = String(data.status || '').toUpperCase()
    if (!(status === 'ACCEPTED' || status === 'CONFIRMED')) return
    loadInvoices()
  }, [meetingOnly, data?.status, data?.selectionId, loadStripeStatus, loadInvoices])

  React.useEffect(() => {
    if (!meetingOnly || !data) return
    const status = String(data.status || '').toUpperCase()
    if (!(status === 'ACCEPTED' || status === 'CONFIRMED')) return
    loadZoomStatus()
    loadGoogleStatus()
    loadCalendarSettings()
  }, [meetingOnly, data?.status, data?.selectionId, loadZoomStatus, loadGoogleStatus, loadCalendarSettings])
  React.useEffect(() => {
    if (!meetingOnly) return
    if (!meetingQueryProviderHint) return
    meetingProviderTouched.current = true
    setMeetingProvider(meetingQueryProviderHint)
  }, [meetingOnly, meetingQueryProviderHint, selectionId])
  React.useEffect(() => {
    if (!meetingOnly) return
    if (meetingProviderTouched.current) return
    if (data?.meeting?.joinUrl) {
      const existingProvider = data.meeting?.provider === 'google' ? 'google' : 'zoom'
      setMeetingProvider(existingProvider)
      return
    }
    if (zoomStatus?.connected) {
      setMeetingProvider('zoom')
      return
    }
    if (googleStatus?.connected) {
      setMeetingProvider('google')
    }
  }, [meetingOnly, data?.meeting?.provider, zoomStatus?.connected, googleStatus?.connected])
  React.useEffect(() => {
    if (!meetingOnly) return
    setMeetingError(null)
    setMeetingNotice(null)
  }, [meetingOnly, data?.selectionId])
  React.useEffect(() => {
    if (!meetingOnly) return
    setMeetingTimeDirty(false)
  }, [meetingOnly, data?.selectionId])
  React.useEffect(() => {
    if (!meetingOnly) return
    const providerMatch = data?.meeting?.provider
      ? data.meeting.provider === meetingProvider
      : meetingProvider === 'zoom'
    if (providerMatch && data?.meeting?.meetingTimeZone) {
      const tz = data.meeting.meetingTimeZone
      if (isValidTimeZone(tz)) {
        setMeetingTimeZone(tz)
      }
    }
  }, [meetingOnly, data?.meeting?.meetingTimeZone, data?.meeting?.provider, meetingProvider])
  React.useEffect(() => {
    if (!meetingOnly || !data) return
    const bookingSlotISO =
      data.slotISO && data.slotISO !== CONTACT_SLOT ? data.slotISO : ''
    const providerMatch = data?.meeting?.provider
      ? data.meeting.provider === meetingProvider
      : meetingProvider === 'zoom'
    const meeting = providerMatch ? data.meeting : null
    const existing = meeting?.meetingTimeISO || bookingSlotISO || ''
    const tzCandidate = meeting?.meetingTimeZone || meetingTimeZone || DEFAULT_TIME_ZONE
    const tz = isValidTimeZone(tzCandidate) ? tzCandidate : DEFAULT_TIME_ZONE
    const shouldUpdateInput = hasBookingSlot || !meetingTimeDirty || Boolean(meeting?.joinUrl)
    if (shouldUpdateInput) {
      setMeetingTimeInput(toTimeZoneInput(existing, tz))
      setMeetingTimeInputZone(tz)
    }
    if (bookingSlotISO) {
      setMeetingTimeConfirmed(Boolean(meeting?.joinUrl))
    } else {
      setMeetingTimeConfirmed(false)
    }
  }, [meetingOnly, data?.selectionId, data?.slotISO, data?.meeting?.meetingTimeISO, data?.meeting?.meetingTimeZone, data?.meeting?.provider, meetingProvider, meetingTimeZone, meetingTimeDirty])

  React.useEffect(() => {
    if (!data) return
    const defaultAmount =
      typeof data.feeCapCents === 'number' && data.feeCapCents > 0
        ? (data.feeCapCents / 100).toFixed(2)
        : ''
    const defaultDescription = data.specialty ? `Booking · ${data.specialty}` : 'Booking'
    setInvoiceDraft((prev) => ({
      amount: prev.amount || defaultAmount,
      description: prev.description || defaultDescription,
      memo: prev.memo || '',
      includeGst: prev.includeGst ?? false,
      abn: prev.abn || '',
      saveAbn: typeof prev.saveAbn === 'boolean' ? prev.saveAbn : true,
      passStripeFee: prev.passStripeFee ?? false,
    }))
  }, [data?.selectionId])

  React.useEffect(() => {
    let cancelled = false
    if (!currentProfession) return
    ;(async () => {
      try {
        const profile = await api.getProviderProfile(currentProfession)
        const abnValue = profile?.abn ? normalizeAbn(String(profile.abn)) : ''
        const providerCountryRaw =
          profile?.address?.countryCode ||
          profile?.address?.country ||
          profile?.adminContact?.address?.countryCode ||
          profile?.adminContact?.address?.country ||
          profile?.publicContact?.address?.countryCode ||
          profile?.publicContact?.address?.country ||
          profile?.countryCode ||
          profile?.country ||
          null
        const providerRegionRaw =
          profile?.address?.state ||
          profile?.address?.province ||
          profile?.address?.region ||
          profile?.adminContact?.address?.state ||
          profile?.adminContact?.address?.province ||
          profile?.adminContact?.address?.region ||
          profile?.publicContact?.address?.state ||
          profile?.publicContact?.address?.province ||
          profile?.publicContact?.address?.region ||
          null
        const providerCountryCode = providerCountryRaw ? normalizeCountryCode(providerCountryRaw) : null
        const providerTaxRule = providerCountryCode ? getTaxRuleForCountry(providerCountryCode, providerRegionRaw) : null
        const providerSupportsTax = Number(providerTaxRule?.percent || 0) > 0
        const providerRequiresAbn = Boolean(providerTaxRule?.requiresAbn) && providerCountryCode === 'AU'
        if (!cancelled) {
          setInvoiceProviderCountryCode(providerCountryCode)
          setInvoiceProviderRegionCode(providerRegionRaw ? String(providerRegionRaw).trim() : null)
          setInvoiceDraft((prev) => ({
            ...prev,
            includeGst: providerSupportsTax ? prev.includeGst ?? false : false,
            abn: providerRequiresAbn ? prev.abn || abnValue : '',
            saveAbn:
              providerRequiresAbn
                ? typeof prev.saveAbn === 'boolean'
                  ? prev.saveAbn
                  : true
                : false,
            passStripeFee: prev.passStripeFee ?? false,
          }))
        }
      } catch (_err) {
        if (!cancelled) {
          setInvoiceProviderCountryCode(null)
          setInvoiceProviderRegionCode(null)
          setInvoiceDraft((prev) => ({
            ...prev,
            includeGst: false,
            abn: '',
            saveAbn: false,
          }))
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [currentProfession])

  React.useEffect(() => { load() }, [load])

  const requestTimeZone = detectTimeZone()
  const formatReviewDate = (iso?: string | null) => {
    if (!iso) return '—'
    const dt = new Date(iso)
    if (!Number.isFinite(dt.getTime())) return '—'
    try {
      return dt.toLocaleString('en-AU', { timeZone: requestTimeZone, dateStyle: 'medium', timeStyle: 'short' })
    } catch {
      return dt.toLocaleString()
    }
  }
  const fmtWhenLocal = (slotISO?: string, createdAt?: string, selectionCreatedAt?: string | null) => {
    const format = (iso?: string) => {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString('en-AU', {
          timeZone: requestTimeZone,
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      } catch {
        return new Date(iso).toLocaleString()
      }
    }
    if (slotISO === CONTACT_SLOT) {
      const submittedAt = createdAt || selectionCreatedAt
      return `Request submitted: ${format(submittedAt)} (${requestTimeZone})`
    }
    return `Requested time: ${format(slotISO)} (${requestTimeZone})`
  }
  const fmtWhenLocalSummary = (slotISO?: string, createdAt?: string, selectionCreatedAt?: string | null) => {
    const format = (iso?: string) => {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString('en-AU', {
          timeZone: requestTimeZone,
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      } catch {
        return new Date(iso).toLocaleString()
      }
    }
    if (slotISO === CONTACT_SLOT) {
      const submittedAt = createdAt || selectionCreatedAt
      return {
        label: 'Submitted',
        value: `${format(submittedAt)} (${requestTimeZone})`,
      }
    }
    return {
      label: 'Requested time',
      value: `${format(slotISO)} (${requestTimeZone})`,
    }
  }
  const toTimeZoneInput = (iso: string | null | undefined, timeZone?: string | null) => {
    if (!iso) return ''
    const zone = timeZone || DEFAULT_TIME_ZONE
    const dt = DateTime.fromISO(iso, { zone })
    if (!dt.isValid) return ''
    return dt.toFormat("yyyy-MM-dd'T'HH:mm")
  }
  const fromTimeZoneInput = (value: string, timeZone?: string | null) => {
    if (!value) return null
    const zone = timeZone || DEFAULT_TIME_ZONE
    const dt = DateTime.fromISO(value, { zone })
    if (!dt.isValid) return null
    return dt.toUTC().toISO()
  }
  const fmtTimeZoneLabel = (iso?: string | null, timeZone?: string | null) => {
    if (!iso) return '—'
    const zone = timeZone || DEFAULT_TIME_ZONE
    try {
      return new Date(iso).toLocaleString('en-AU', {
        timeZone: zone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    } catch {
      return new Date(iso).toLocaleString()
    }
  }

  const fmtInvoiceAmount = (cents?: number | null, currency?: string | null) => {
    if (!Number.isFinite(cents || 0)) return '—'
    const amt = (Number(cents) / 100).toFixed(2)
    const cur = (currency || 'aud').toUpperCase()
    return `${amt} ${cur}`
  }
  const fmtFeeAmount = (value?: number | null) => {
    if (!Number.isFinite(value || 0)) return '—'
    return formatCurrencyAmount(Number(value), invoiceCurrencyCode)
  }

  const fmtInvoiceStatus = (status?: string | null) => {
    const s = String(status || '').toLowerCase()
    if (s === 'paid') return 'Paid'
    if (s === 'open') return 'Sent'
    if (s === 'draft') return 'Draft'
    if (s === 'void' || s === 'voided') return 'Voided'
    if (s === 'uncollectible') return 'Uncollectible'
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown'
  }
  const invoiceStatusClass = (status?: string | null) => {
    const s = String(status || '').toLowerCase()
    if (s === 'paid') return 'paid'
    if (s === 'open') return 'sent'
    if (s === 'void' || s === 'voided' || s === 'uncollectible') return 'void'
    if (s === 'draft') return 'draft'
    return 'pending'
  }
  const renderInvoiceIcon = (status: 'paid' | 'sent') => {
    const baseIcon = status === 'paid'
      ? <CheckCircle2 size={16} strokeWidth={2} aria-hidden="true" />
      : <FileText size={16} strokeWidth={2} aria-hidden="true" />
    return (
      <span className="invoice-icon-stack" aria-hidden="true">
        {baseIcon}
        <span className="invoice-icon-overlay" aria-hidden="true">
          <DollarSign size={10} strokeWidth={2.2} />
        </span>
      </span>
    )
  }
  const renderInvoiceStatus = (status?: string | null) => {
    const s = String(status || '').toLowerCase()
    if (s === 'paid') {
      return (
        <span
          className="invoice-status-icon paid instant-tooltip"
          data-tooltip="Invoice paid"
          aria-label="Invoice paid"
        >
          {renderInvoiceIcon('paid')}
        </span>
      )
    }
    if (s === 'open' || s === 'sent') {
      return (
        <span
          className="invoice-status-icon sent instant-tooltip"
          data-tooltip="Invoice sent"
          aria-label="Invoice sent"
        >
          {renderInvoiceIcon('sent')}
        </span>
      )
    }
    return (
      <span className={`invoice-status ${invoiceStatusClass(status)}`}>
        {fmtInvoiceStatus(status)}
      </span>
    )
  }

  const sortedInvoices = React.useMemo(() => {
    const rows = Array.isArray(invoiceList) ? [...invoiceList] : []
    return rows.sort((a, b) => {
      const tsA = Date.parse(a.paidAt || a.sentAt || a.createdAt || '') || 0
      const tsB = Date.parse(b.paidAt || b.sentAt || b.createdAt || '') || 0
      return tsB - tsA
    })
  }, [invoiceList])

  const accept = () => {
    if (!data) return
    if (acceptancePerk.reason) {
      setWaiverConfirmOpen(true)
      return
    }
    setNeedCard(true)
    setCardErr(null)
  }

  const submitCardAndAccept = async () => {
    if (!stripe || !elements || !data) return
    if (slotElapsed) {
      alert('This request has expired because the appointment time has already passed.')
      setNeedCard(false)
      return
    }

    setCardBusy(true); setCardErr(null)
    try {
      const card = elements.getElement(CardElement)
      if (!card) throw new Error('Card element not ready')

      const pmRes = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: cardName ? { name: cardName } : undefined,
      })
      if (pmRes.error || !pmRes.paymentMethod?.id) {
        throw new Error(pmRes.error?.message || 'Failed to create payment method')
      }

      await api.acceptSelection(currentProfession, data.selectionId, undefined, pmRes.paymentMethod.id)
      setFlash({ type: 'success', message: 'Accepted. Booking confirmed and the client has been notified.' })
      flashTimerRef.current = window.setTimeout(() => exitToInbox(), 1400)
    } catch (e:any) {
      const msg = String(e?.message || '')
      if (msg.includes('Already booked') || msg.includes('Already decided')) {
        setFlash({ type: 'error', message: 'Already filled by another provider. Returning to your dashboard.' })
        flashTimerRef.current = window.setTimeout(() => exitToInbox(), 1400)
      } else if (msg.includes('Card not on file')) {
        setCardErr('The client has not set up a card for this selection yet.')
      } else if (msg.includes('Fee exceeds')) {
        setCardErr('Fee exceeds the client’s cap.')
      } else {
        setCardErr(msg || 'Failed to accept with this card')
      }
    } finally {
      setCardBusy(false)
    }
  }

  const acceptWithWaiver = async () => {
    if (!data) return
    setBusy(true)
    try {
      await api.acceptSelection(currentProfession, data.selectionId)
      setFlash({ type: 'success', message: 'Accepted. Booking confirmed and the client has been notified.' })
      flashTimerRef.current = window.setTimeout(() => exitToInbox(), 1400)
    } catch (e: any) {
      const msg = String(e?.message || '')
      if (msg.includes('Already booked') || msg.includes('Already decided')) {
        setFlash({ type: 'error', message: 'Already filled by another provider. Returning to your dashboard.' })
        flashTimerRef.current = window.setTimeout(() => exitToInbox(), 1400)
      } else {
        alert(msg || 'Failed to accept request.')
      }
    } finally {
      setBusy(false)
    }
  }
  const confirmWaiverAndAccept = async () => {
    await acceptWithWaiver()
    setWaiverConfirmOpen(false)
  }

  const decline = async () => {
    if (!data) return
    if (!confirm('Decline this selection?')) return
    setBusy(true)
    try {
      await atlas.declineCandidate(currentProfession, data.selectionId)
      exitToInbox()
    } catch (e:any) {
      alert(e?.message || 'Failed to decline')
    } finally {
      setBusy(false)
    }
  }
  const markCompleted = async () => {
    if (!data) return
    if (!confirm('Mark this job as completed?')) return
    setBusy(true)
    try {
      await atlas.completeSelection(currentProfession, data.selectionId)
      await load()
      setFlash({ type: 'success', message: 'Marked completed. Funds will release after client confirmation or auto-release window.' })
    } catch (e: any) {
      alert(e?.message || 'Failed to mark completed')
    } finally {
      setBusy(false)
    }
  }
  const reviewReady =
    data?.status === 'ACCEPTED' && (slotElapsed || data?.slotISO === CONTACT_SLOT)
  const stripeHasToken = stripeStatus?.hasAccessToken !== false
  const stripeNeedsReconnect = stripeStatus?.connected && stripeStatus?.hasAccessToken === false
  const stripeReady =
    stripeStatus?.connected &&
    stripeStatus?.chargesEnabled &&
    stripeStatus?.payoutsEnabled &&
    stripeHasToken &&
    (!stripeStatus?.accountType || stripeStatus?.accountType === 'standard')
  const stripeNeedsSetup = stripeStatus?.connected && !stripeReady && !stripeNeedsReconnect
  const stripeNeedsStandard =
    stripeStatus?.connected &&
    stripeStatus?.accountType &&
    stripeStatus?.accountType !== 'standard'
  const invoiceSentRecently = invoiceSentAt != null
  const invoiceCountryCode = invoiceProviderCountryCode ? normalizeCountryCode(invoiceProviderCountryCode) : null
  const invoiceCountryLabel = invoiceCountryCode ? getCountryLabel(invoiceCountryCode) : 'this provider'
  const invoiceCurrencyCode = invoiceCountryCode ? getCurrencyForCountry(invoiceCountryCode) : 'AUD'
  const invoiceTaxRule = invoiceCountryCode ? getTaxRuleForCountry(invoiceCountryCode, invoiceProviderRegionCode) : null
  const invoiceTaxPercent = Number(invoiceTaxRule?.percent || 0)
  const invoiceTaxLabel = invoiceTaxRule?.label || 'Tax'
  const invoiceSupportsTax = invoiceTaxPercent > 0
  const invoiceTaxInclusive = invoiceTaxRule?.inclusive !== false
  const invoiceTaxRequiresAbn = Boolean(invoiceTaxRule?.requiresAbn) && invoiceCountryCode === 'AU'
  const feeEstimateRule = getStripeFeeRuleForCountry(invoiceCountryCode || 'AU')
  const abnNormalized = normalizeAbn(invoiceDraft.abn)
  const abnInvalid =
    invoiceTaxRequiresAbn && invoiceDraft.includeGst && invoiceDraft.abn && !isValidAbn(abnNormalized)
  const abnRequired = invoiceTaxRequiresAbn && invoiceDraft.includeGst && !abnNormalized
  const handleSubmitClientReview = async () => {
    if (!data) return
    const selectionId = data.selectionId
    const patientId = data.patientId
    if (!selectionId || !patientId) {
      setReviewError('Missing data for this rating.')
      return
    }
    if (clientReviewDraft.rating < 1) {
      setReviewError('Select a rating before submitting.')
      return
    }
    setReviewBusy(true)
    setReviewError(null)
    setReviewNotice(null)
    try {
      await api.submitClientReview(currentProfession, {
        selectionId,
        patientId,
        rating: clientReviewDraft.rating,
      })
      setReviewNotice('Thanks — your rating was saved.')
      setData((prev) => prev ? ({ ...prev, review: { client: { submitted: true } } }) : prev)
    } catch (e: any) {
      setReviewError(e?.message || 'Failed to submit rating.')
    } finally {
      setReviewBusy(false)
    }
  }
  const handleSubmitProviderReply = async () => {
    if (!data) return
    const selectionId = data.selectionId
    const patientId = data.patientId
    if (!selectionId || !patientId) {
      setReplyError('Missing data for this reply.')
      return
    }
    if (!data.reviewReceived?.submitted) {
      setReplyError('Client review not found yet.')
      return
    }
    const comment = providerReplyDraft.trim()
    if (!comment) {
      setReplyError('Type a reply before posting.')
      return
    }
    setReplyBusy(true)
    setReplyError(null)
    setReplyNotice(null)
    try {
      const res = await api.replyToSpecialistReview(currentProfession, {
        selectionId,
        patientId,
        comment,
      })
      const providerReply = {
        comment,
        ...(res?.providerReply || {}),
      }
      setData((prev) =>
        prev
          ? {
              ...prev,
              reviewReceived: {
                ...(prev.reviewReceived || {}),
                submitted: true,
                providerReply,
              },
            }
          : prev
      )
      setProviderReplyDraft(comment)
      setReplyNotice('Reply saved. It is now live on your public EUCA Match profile, and the client was notified by email.')
    } catch (e: any) {
      setReplyError(e?.message || 'Failed to save reply.')
    } finally {
      setReplyBusy(false)
    }
  }
  const handleOpenNotes = () => {
    if (!data) return
    if (onOpenNotes) {
      onOpenNotes(data.selectionId)
      if (inline && onClose) onClose()
      return
    }
    nav(`/dashboard?stack=${currentProfession}&notes=${encodeURIComponent(data.selectionId)}`)
  }
  const handleOpenPayment = () => {
    if (!data || !onOpenPayment) return
    onOpenPayment(data.selectionId)
    if (inline && onClose) onClose()
  }
  const handleOpenDetails = () => {
    if (!data || !onOpenDetails) return
    onOpenDetails(data.selectionId)
    if (inline && onClose) onClose()
  }
  const handleOpenMeeting = () => {
    if (!data) return
    if (onOpenMeeting) {
      onOpenMeeting(data.selectionId)
      if (inline && onClose) onClose()
      return
    }
    nav(`/dashboard?stack=${currentProfession}&meeting=${encodeURIComponent(data.selectionId)}`)
  }
  const handleOpenReview = () => {
    if (!data || !onOpenReview) return
    onOpenReview(data.selectionId)
    if (inline && onClose) onClose()
  }
  const handleOpenReviewReminder = () => {
    if (!data || !onOpenReviewReminder) return
    onOpenReviewReminder(data.selectionId)
    if (inline && onClose) onClose()
  }

  const handleConnectStripe = async () => {
    try {
      const res = await api.stripeOnboard(`/dashboard?stack=${currentProfession}`)
      if (res?.url) window.location.href = res.url
    } catch (err: any) {
      setInvoiceError(err?.message || 'Failed to start Stripe onboarding')
    }
  }

  const handleDisconnectStripe = async () => {
    setStripeDisconnecting(true)
    setStripeStatusError(null)
    setInvoiceError(null)
    try {
      await api.stripeDisconnect()
      setStripeStatus({ connected: false })
      setInvoiceList([])
    } catch (err: any) {
      setStripeStatusError(err?.message || 'Failed to disconnect Stripe')
    } finally {
      setStripeDisconnecting(false)
    }
  }

  const handleConnectZoom = async () => {
    if (!data) return
    setMeetingError(null)
    try {
      const returnTo =
        `/dashboard?stack=${currentProfession}&meeting=${encodeURIComponent(data.selectionId)}&meetingProvider=zoom`
      const res = await api.zoomStart(currentProfession, returnTo)
      if (res?.url) window.location.href = res.url
    } catch (err: any) {
      setMeetingError(err?.message || 'Failed to start Zoom connection')
    }
  }

  const handleConnectGoogle = async () => {
    if (!data) return
    setMeetingError(null)
    try {
      const returnTo =
        `/dashboard?stack=${currentProfession}&meeting=${encodeURIComponent(data.selectionId)}&meetingProvider=google`
      const res = await api.googleCalendarStart(currentProfession, returnTo)
      if (res?.url) window.location.href = res.url
    } catch (err: any) {
      setMeetingError(err?.message || 'Failed to start Google Calendar connection')
    }
  }

  const handleDisconnectZoom = async () => {
    setZoomDisconnecting(true)
    setZoomError(null)
    setMeetingError(null)
    setMeetingNotice(null)
    try {
      await api.zoomDisconnect(currentProfession)
      setZoomStatus({ connected: false })
      setZoomAutoCreate(false)
      setMeetingNotice('Zoom disconnected.')
    } catch (err: any) {
      setZoomError(err?.message || 'Failed to disconnect Zoom')
    } finally {
      setZoomDisconnecting(false)
    }
  }

  const handleDisconnectGoogle = async () => {
    setGoogleDisconnecting(true)
    setGoogleError(null)
    setMeetingError(null)
    setMeetingNotice(null)
    try {
      await api.googleCalendarDisconnect(currentProfession)
      setGoogleStatus({ connected: false })
      setGoogleAutoCreate(false)
      setMeetingNotice('Google Calendar disconnected.')
    } catch (err: any) {
      setGoogleError(err?.message || 'Failed to disconnect Google Calendar')
    } finally {
      setGoogleDisconnecting(false)
    }
  }

  const handleToggleZoomAutoCreate = async (next: boolean) => {
    setZoomAutoCreate(next)
    setZoomError(null)
    setZoomSaving(true)
    try {
      await api.zoomSettings(currentProfession, { autoCreateMeetings: next })
      setZoomStatus((prev) => (prev ? { ...prev, autoCreateMeetings: next } : prev))
    } catch (err: any) {
      setZoomAutoCreate((prev) => !next)
      setZoomError(err?.message || 'Failed to update Zoom settings')
    } finally {
      setZoomSaving(false)
    }
  }

  const handleToggleGoogleAutoCreate = async (next: boolean) => {
    setGoogleAutoCreate(next)
    setGoogleError(null)
    setGoogleSaving(true)
    try {
      await api.googleCalendarSettings(currentProfession, { autoCreateMeetings: next })
      setGoogleStatus((prev) => (prev ? { ...prev, autoCreateMeetings: next } : prev))
    } catch (err: any) {
      setGoogleAutoCreate((prev) => !next)
      setGoogleError(err?.message || 'Failed to update Google Calendar settings')
    } finally {
      setGoogleSaving(false)
    }
  }


  const handleCreateMeeting = async () => {
    if (!data) return
    const bookingSlotISO =
      data.slotISO && data.slotISO !== CONTACT_SLOT ? data.slotISO : ''
    const hasBookingSlot = Boolean(bookingSlotISO)
    const rawZone = data.meeting?.meetingTimeZone || meetingTimeZone
    const resolvedTimeZone = isValidTimeZone(rawZone || '') ? (rawZone as string) : DEFAULT_TIME_ZONE
    const parseZone = isValidTimeZone(meetingTimeInputZone) ? meetingTimeInputZone : resolvedTimeZone
    let meetingTimeISO: string | null = null
    if (hasBookingSlot) {
      if (!meetingTimeConfirmed) {
        setMeetingError('Confirm the meeting time before creating the link.')
        return
      }
      meetingTimeISO = bookingSlotISO
    } else {
      if (!meetingTimeInput) {
        setMeetingError('Enter a meeting time to continue.')
        return
      }
      const parsed = fromTimeZoneInput(meetingTimeInput, parseZone)
      if (!parsed) {
        setMeetingError('Enter a valid meeting time.')
        return
      }
      if (Date.parse(parsed) <= Date.now()) {
        setMeetingError('Meeting time must be in the future.')
        return
      }
      meetingTimeISO = parsed
    }
    if (meetingProvider === 'zoom' && !zoomStatus?.connected) {
      setMeetingError('Connect Zoom to create a meeting link.')
      return
    }
    if (meetingProvider === 'google' && !googleStatus?.connected) {
      setMeetingError('Connect Google Calendar to create a Google Meet link.')
      return
    }
    if (meetingProvider === 'google' && !googleWriteReady) {
      setMeetingError('Select a Google calendar in Calendar Sync to enable Google Meet.')
      return
    }
    setMeetingBusy(true)
    setMeetingError(null)
    setMeetingNotice(null)
    try {
      const res = await atlas.createSelectionMeeting(
        currentProfession,
        data.selectionId,
        meetingTimeISO || undefined,
        resolvedTimeZone,
        meetingProvider,
      )
      const meeting = res?.meeting || null
      if (meeting) {
        setData((prev) => {
          if (!prev) return prev
          const next = { ...prev, meeting }
          if (res?.slotISO) next.slotISO = res.slotISO
          return next
        })
      }
      setMeetingNotice('Meeting link created and emailed to the client.')
    } catch (err: any) {
      setMeetingError(err?.message || 'Failed to create meeting link')
    } finally {
      setMeetingBusy(false)
    }
  }

  const handleResendMeeting = async () => {
    if (!data) return
    setMeetingBusy(true)
    setMeetingError(null)
    setMeetingNotice(null)
    try {
      const res = await atlas.resendSelectionMeeting(currentProfession, data.selectionId)
      const meeting = res?.meeting || null
      if (meeting) {
        setData((prev) => (prev ? { ...prev, meeting } : prev))
      }
      setMeetingNotice('Meeting link resent to the client.')
    } catch (err: any) {
      setMeetingError(err?.message || 'Failed to resend meeting link')
    } finally {
      setMeetingBusy(false)
    }
  }

  const handleCopyMeetingLink = async (link?: string | null, label?: string) => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setMeetingNotice(`${label || 'Link'} copied.`)
    } catch {
      setMeetingError('Copy failed. Please copy the link manually.')
    }
  }

  const handleSendInvoice = async () => {
    if (!data) return
    const amountValue = Number(invoiceDraft.amount)
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      setInvoiceError('Enter a valid amount')
      return
    }
    const includeTax = invoiceSupportsTax && !!invoiceDraft.includeGst
    const includeGst = includeTax && invoiceCountryCode === 'AU'
    if (invoiceDraft.includeGst && !invoiceSupportsTax) {
      setInvoiceError(`${invoiceTaxLabel} is not available for ${invoiceCountryLabel} providers`)
      return
    }
    const abnValue = normalizeAbn(invoiceDraft.abn)
    if (invoiceTaxRequiresAbn && includeTax) {
      if (!abnValue) {
        setInvoiceError('ABN is required to include tax')
        return
      }
      if (!isValidAbn(abnValue)) {
        setInvoiceError('Enter a valid ABN')
        return
      }
    }
    const amountCents = Math.round(amountValue * 100)
    setInvoiceBusy(true)
    setInvoiceError(null)
    try {
      const res = await atlas.createSelectionInvoice(currentProfession, data.selectionId, {
        amountCents,
        description: invoiceDraft.description.trim() || undefined,
        memo: invoiceDraft.memo.trim() || undefined,
        includeGst,
        abn: invoiceTaxRequiresAbn && includeTax ? abnValue : undefined,
        saveAbn: invoiceTaxRequiresAbn && includeTax ? invoiceDraft.saveAbn !== false : undefined,
        passStripeFee: !!invoiceDraft.passStripeFee,
      })
      if (res?.invoice) {
        setInvoiceList((prev) => {
          const rest = prev.filter((inv) => inv.invoiceId !== res.invoice.invoiceId)
          return [res.invoice, ...rest]
        })
      } else {
        await loadInvoices()
      }
      const sentStamp = new Date()
      setInvoiceSentAt(sentStamp.getTime())
      showInvoiceNotice(
        'success',
        `Invoice sent · ${sentStamp.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}`,
      )
    } catch (err: any) {
      setInvoiceError(err?.message || 'Failed to send invoice')
    } finally {
      setInvoiceBusy(false)
    }
  }

  const handleResendInvoice = async (invoiceId: string) => {
    setInvoiceBusy(true)
    setInvoiceError(null)
    try {
      const res = await atlas.resendInvoice(currentProfession, invoiceId)
      if (res?.invoice) {
        setInvoiceList((prev) =>
          prev.map((inv) => (inv.invoiceId === invoiceId ? { ...inv, ...res.invoice } : inv))
        )
      } else {
        await loadInvoices()
      }
      const sentStamp = new Date()
      setInvoiceSentAt(sentStamp.getTime())
      showInvoiceNotice(
        'success',
        `Invoice resent · ${sentStamp.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}`,
      )
    } catch (err: any) {
      setInvoiceError(err?.message || 'Failed to resend invoice')
    } finally {
      setInvoiceBusy(false)
    }
  }

  const handleVoidInvoice = async (invoiceId: string) => {
    if (!confirm('Void this invoice?')) return
    setInvoiceBusy(true)
    setInvoiceError(null)
    try {
      const res = await atlas.voidInvoice(currentProfession, invoiceId)
      if (res?.invoice) {
        setInvoiceList((prev) =>
          prev.map((inv) => (inv.invoiceId === invoiceId ? { ...inv, ...res.invoice } : inv))
        )
      } else {
        await loadInvoices()
      }
      const sentStamp = new Date()
      showInvoiceNotice(
        'success',
        `Invoice voided · ${sentStamp.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}`,
      )
    } catch (err: any) {
      setInvoiceError(err?.message || 'Failed to void invoice')
    } finally {
      setInvoiceBusy(false)
    }
  }

  const backControl = inline ? (
    <button className="btn" onClick={exitToInbox}>Close</button>
  ) : (
    <Link className="btn" to={`/dashboard?stack=${currentProfession}`}>Back</Link>
  )

  if (loading) return <div className="container"><Spinner label="Loading…" /></div>
  if (err) return (
    <div className="container">
      <div className="banner error" style={{ marginBottom: 12 }}>{err}</div>
      {backControl}
    </div>
  )
  if (!data) return (
    <div className="container">
      <p>Not found.</p>
      {backControl}
    </div>
  )

  const statusValue = String(data.status || '').toUpperCase()
  const isConfirmed = statusValue === 'ACCEPTED' || statusValue === 'CONFIRMED'
  const invoiceBadge = (() => {
    if (!isConfirmed) return null
    const s = String(data.invoiceStatus || '').toLowerCase()
    if (s === 'paid') return { status: 'paid' as const, className: 'paid', title: 'Invoice paid' }
    if (s === 'open' || s === 'sent') return { status: 'sent' as const, className: 'sent', title: 'Invoice sent' }
    return null
  })()
  const reviewBadge = (() => {
    if (!isConfirmed) return null
    const submitted = Boolean(data.reviewReceived?.submitted)
    return {
      submitted,
      className: submitted ? 'review-received' : 'review-pending',
      title: submitted ? 'Client review received' : 'Client review pending',
    }
  })()
  const meeting =
    data.meeting && (data.meeting.provider
      ? data.meeting.provider === meetingProvider
      : meetingProvider === 'zoom')
      ? data.meeting
      : null
  const bookingSlotISO =
    data.slotISO && data.slotISO !== CONTACT_SLOT ? data.slotISO : ''
  const hasBookingSlot = Boolean(bookingSlotISO)
  const meetingTimeISO = meeting?.meetingTimeISO || bookingSlotISO || null
  const meetingTimeZoneLabelCandidate = meeting?.meetingTimeZone || meetingTimeZone || DEFAULT_TIME_ZONE
  const meetingTimeZoneLabel = isValidTimeZone(meetingTimeZoneLabelCandidate)
    ? meetingTimeZoneLabelCandidate
    : DEFAULT_TIME_ZONE
  const meetingTimeLabel = meetingTimeISO ? fmtTimeZoneLabel(meetingTimeISO, meetingTimeZoneLabel) : '—'
  const fmtMeetingStamp = (iso?: string | null) => {
    if (!iso) return '—'
    const dt = new Date(iso)
    if (!Number.isFinite(dt.getTime())) return '—'
    return dt.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
  }
  const meetingProviderLabel = meetingProvider === 'google' ? 'Google Meet' : 'Zoom'
  const zoomAccountConnected = Boolean(zoomStatus?.connected)
  const googleAccountConnected = Boolean(googleStatus?.connected)
  const meetingProviderConnected =
    Boolean(meeting?.joinUrl) ||
    (meetingProvider === 'google' ? googleAccountConnected : zoomAccountConnected)
  const googleNeedsReconnect = meetingProvider === 'google' && !googleAccountConnected && Boolean(googleStatus?.reason)
  const googleWriteReady = meetingProvider === 'google' && googleAccountConnected && (
    Boolean(googleStatus?.writeCalendarId) ||
    (Array.isArray(googleStatus?.selectedCalendarIds) && googleStatus.selectedCalendarIds.length > 0)
  )
  const canShowPaymentLink = statusValue === 'PENDING' || isConfirmed
  const canShowMeetingLink = statusValue === 'PENDING' || isConfirmed
  const showPaymentSection = !meetingOnly && (paymentOnly || !inline)

  const stripeFeeRule = getStripeFeeRuleForCountry(invoiceCountryCode || 'AU')
  const calcStripeFeeCents = (valueCents: number) =>
    Math.max(0, Math.round(valueCents * stripeFeeRule.rate + stripeFeeRule.fixedCents))
  const grossUpStripeFeeCents = (targetCents: number) => {
    if (!Number.isFinite(targetCents) || targetCents <= 0) return targetCents
    let gross = Math.ceil((targetCents + stripeFeeRule.fixedCents) / (1 - stripeFeeRule.rate))
    for (let i = 0; i < 6; i += 1) {
      const fee = calcStripeFeeCents(gross)
      if (gross - fee >= targetCents) break
      gross += 1
    }
    return gross
  }

  const amountNumber = Number(invoiceDraft.amount)
  const amountValid = Number.isFinite(amountNumber) && amountNumber > 0
  const amountCents = amountValid ? Math.round(amountNumber * 100) : null
  const passStripeFee = !!invoiceDraft.passStripeFee
  const grossCents = amountCents != null
    ? (passStripeFee ? grossUpStripeFeeCents(amountCents) : amountCents)
    : null
  const feeCents = grossCents != null
    ? calcStripeFeeCents(grossCents)
    : null
  const surchargeCents = passStripeFee && amountCents != null && grossCents != null
    ? Math.max(0, grossCents - amountCents)
    : null
  const feeTaxCents = (() => {
    if (feeCents == null || !invoiceSupportsTax) return null
    if (!Number.isFinite(invoiceTaxPercent) || invoiceTaxPercent <= 0) return null
    if (invoiceTaxInclusive) {
      return Math.round((feeCents * invoiceTaxPercent) / (100 + invoiceTaxPercent))
    }
    return Math.round((feeCents * invoiceTaxPercent) / 100)
  })()
  const feeProcessingCents =
    feeCents != null
      ? Math.max(0, feeCents - (invoiceTaxInclusive ? (feeTaxCents || 0) : 0))
      : null
  const netCents = grossCents != null && feeCents != null
    ? Math.max(0, grossCents - feeCents)
    : null
  const netDisplayCents = passStripeFee && amountCents != null ? amountCents : netCents
  const providerReply = data.reviewReceived?.providerReply
  const clientDisplayName =
    (data.patientName || data.patientContact?.name || '').trim() || 'Client'
  const summaryTime = fmtWhenLocalSummary(data.slotISO, data.createdAt, data.selectionCreatedAt)

  return (
    <div className="page inbox-detail-page">
      <div className="header">
        <h1 className="section-title" style={{ margin: 0 }}>
          {meetingOnly ? 'Meeting' : paymentOnly ? 'Payment' : 'Request'}
        </h1>
        <div className="inline-gap-2">
          {meetingOnly ? (
            <>
              {onOpenDetails && (
                <button className="btn btn-details" type="button" onClick={handleOpenDetails}>
                  Client details
                </button>
              )}
              {onOpenNotes && (
                <button
                  className="btn btn-notes instant-tooltip"
                  type="button"
                  onClick={handleOpenNotes}
                  data-tooltip="AI tools available in Notes"
                >
                  Notes
                  <span className="notes-ai-badge" aria-hidden="true">AI</span>
                </button>
              )}
              {canShowPaymentLink && (
                <button className="btn btn-payment" type="button" onClick={handleOpenPayment}>
                  Payment
                </button>
              )}
            </>
          ) : paymentOnly ? (
            <>
              {onOpenDetails && (
                <button className="btn btn-details" type="button" onClick={handleOpenDetails}>
                  Client details
                </button>
              )}
              {onOpenNotes && (
                <button
                  className="btn btn-notes instant-tooltip"
                  type="button"
                  onClick={handleOpenNotes}
                  data-tooltip="AI tools available in Notes"
                >
                  Notes
                  <span className="notes-ai-badge" aria-hidden="true">AI</span>
                </button>
              )}
              {canShowMeetingLink && (
                <button className="btn btn-meeting" type="button" onClick={handleOpenMeeting}>
                  Meeting
                </button>
              )}
            </>
          ) : (
            <>
              <button
                className="btn btn-notes instant-tooltip"
                type="button"
                onClick={handleOpenNotes}
                data-tooltip="AI tools available in Notes"
              >
                Notes
                <span className="notes-ai-badge" aria-hidden="true">AI</span>
              </button>
              {canShowPaymentLink && (
                <button
                  className="btn btn-payment"
                  type="button"
                  onClick={inline && onOpenPayment ? handleOpenPayment : scrollToPayment}
                >
                  Payment
                </button>
              )}
              {canShowMeetingLink && (
                <button className="btn btn-meeting" type="button" onClick={handleOpenMeeting}>
                  Meeting
                </button>
              )}
            </>
          )}
          {backControl}
        </div>
      </div>
      {flash && (
        <div className={`banner ${flash.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: 12 }}>
          {flash.message}
      </div>
    )}
      {waiverConfirmOpen && (
        <div className="overlay show inbox-detail-overlay" onClick={() => !busy && setWaiverConfirmOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="sheet-header">
              <div className="sheet-title">Confirm acceptance</div>
              <button className="btn close" onClick={() => setWaiverConfirmOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="sheet-body">
              <p>
                Acceptance is free for all professionals. Confirm to accept and continue in the in-app chat box.
              </p>
              <ul className="hint" style={{ marginLeft: '1rem' }}>
                <li>No payment will be taken.</li>
                <li>Contact details stay private. Use the in-app chat for all communication.</li>
              </ul>
            </div>
            <div className="sheet-footer">
              <button className="btn" onClick={() => setWaiverConfirmOpen(false)} disabled={busy}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={confirmWaiverAndAccept} disabled={busy}>
                {busy ? 'Accepting…' : 'Confirm & accept'}
              </button>
            </div>
          </div>
        </div>
      )}

      {summaryOnly ? (
        <div className="card payment-summary" style={{ marginBottom: 16 }}>
          <div className="payment-summary-head">
            <div>
              <div className="payment-summary-title">Client</div>
              <div className="payment-summary-sub">
                {clientDisplayName}
              </div>
            </div>
            <span className={`status-pill ${statusMeta.className}`}>{statusMeta.label}</span>
          </div>
          <div className="payment-summary-row">
            <div className="payment-summary-label">{summaryTime.label}</div>
            <div className="payment-summary-value">{summaryTime.value}</div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="list">
            <div><b>Specialty:</b> {data.specialty}</div>
            <div><b>Time:</b> {fmtWhenLocal(data.slotISO, data.createdAt, data.selectionCreatedAt)}</div>
            <div>
              <b>Status:</b>{' '}
              <span className="inline-gap-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <span className={`status-pill ${statusMeta.className}`}>{statusMeta.label}</span>
                {invoiceBadge && (
                  <button
                    type="button"
                    className={`invoice-status-icon instant-tooltip ${invoiceBadge.className}`}
                    data-tooltip={invoiceBadge.title}
                    aria-label={invoiceBadge.title}
                    onClick={() => {
                      if (inline && onOpenPayment) {
                        handleOpenPayment()
                      } else {
                        scrollToPayment()
                      }
                    }}
                  >
                    {renderInvoiceIcon(invoiceBadge.status)}
                  </button>
                )}
                {reviewBadge && (
                  <button
                    type="button"
                    className={`review-status instant-tooltip ${reviewBadge.className}`}
                    data-tooltip={reviewBadge.title}
                    aria-label={reviewBadge.title}
                    onClick={() => {
                      if (reviewBadge.submitted) {
                        handleOpenReview()
                      } else {
                        handleOpenReviewReminder()
                      }
                    }}
                  >
                    <span className="review-icon-stack" aria-hidden="true">
                      <Star size={16} strokeWidth={2} />
                      {reviewBadge.submitted ? null : null}
                    </span>
                  </button>
                )}
              </span>
              {slotElapsed && (
                <span className="hint" style={{ marginLeft: 8 }}>
                  Requested time has already passed.
                </span>
              )}
            </div>
            {data.feeCapCents != null && (
              <div><b>Client fee cap:</b> {fmtInvoiceAmount(data.feeCapCents, invoiceCurrencyCode)}</div>
            )}
            <div style={{ whiteSpace:'pre-wrap' }}>
              <b>Client note:</b> {data.reason || 'No additional details'}
            </div>
            {data.patientSummary && (
              <>
                {data.patientSummary.ageYears != null && (
                  <div><b>Client age:</b> {data.patientSummary.ageYears} years</div>
                )}
                {data.patientSummary.gender && (
                  <div><b>Client gender:</b> {data.patientSummary.gender}</div>
                )}
                {data.patientSummary.state && (
                  <div><b>State:</b> {data.patientSummary.state}</div>
                )}
              </>
            )}
            {clientRatingReady && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <b>Client rating:</b>
                {clientRatingSummary?.ratingCount ? (
                  <>
                    <RatingStarsDisplay value={clientRatingSummary.ratingAverage || 0} showValue />
                    <span className="hint">
                      {clientRatingSummary.ratingCount} review{clientRatingSummary.ratingCount === 1 ? '' : 's'}
                    </span>
                  </>
                ) : (
                  <span className="hint">No reviews yet</span>
                )}
              </div>
            )}
            {(() => {
              const msg =
                acceptancePerk.label ||
                'Contact details stay private. Use the in-app chat box for all communication.';
              return msg ? (
                <div className="minor" style={{ marginTop: 8 }}>
                  {msg}
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {meetingOnly && (
        <section className="section">
          <div className="section-title">Meeting link</div>
          <div className="card">
            <div className="chip-row" role="tablist" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className={`chip ${meetingProvider === 'zoom' ? 'active' : ''}`}
                onClick={() => {
                  meetingProviderTouched.current = true
                  setMeetingProvider('zoom')
                }}
              >
                Zoom
              </button>
              <button
                type="button"
                className={`chip ${meetingProvider === 'google' ? 'active' : ''}`}
                onClick={() => {
                  meetingProviderTouched.current = true
                  setMeetingProvider('google')
                }}
              >
                Google Meet
              </button>
            </div>
            {(meetingProvider === 'zoom' ? zoomLoading : googleLoading) && (
              <Spinner label={`Checking ${meetingProviderLabel} status…`} />
            )}
            {meetingProvider === 'zoom' && zoomError && (
              <div className="banner error" style={{ marginBottom: 12 }}>
                {zoomError}
              </div>
            )}
            {meetingProvider === 'google' && googleError && (
              <div className="banner error" style={{ marginBottom: 12 }}>
                {googleError}
              </div>
            )}
            {!isConfirmed && (
              <div className="banner warning" style={{ marginBottom: 12 }}>
                Meeting links are available for confirmed bookings only.
              </div>
            )}
            {isConfirmed && !(meetingProvider === 'zoom' ? zoomLoading : googleLoading) && (
              <>
                {!meetingProviderConnected ? (
                  <>
                    <div className="banner info" style={{ marginBottom: 12 }}>
                      {meetingProvider === 'zoom'
                        ? 'Connect Zoom to generate meeting links for this booking.'
                        : 'Connect Google Calendar to generate Google Meet links for this booking.'}
                    </div>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={meetingProvider === 'zoom' ? handleConnectZoom : handleConnectGoogle}
                    >
                      {meetingProvider === 'zoom'
                        ? 'Connect Zoom'
                        : googleNeedsReconnect
                          ? 'Reconnect Google Calendar'
                          : 'Connect Google Calendar'}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="meeting-connection-row">
                      <div className="hint">
                        Connected as{' '}
                        {meetingProvider === 'zoom'
                          ? (zoomStatus?.name || zoomStatus?.email || 'your Zoom account')
                          : (googleStatus?.email || 'your Google account')}
                        .
                      </div>
                      <button
                        type="button"
                        className="btn-text meeting-disconnect-btn"
                        onClick={meetingProvider === 'zoom' ? handleDisconnectZoom : handleDisconnectGoogle}
                        disabled={meetingProvider === 'zoom' ? zoomDisconnecting : googleDisconnecting}
                      >
                        {meetingProvider === 'zoom'
                          ? (zoomDisconnecting ? 'Disconnecting…' : 'Disconnect Zoom')
                          : (googleDisconnecting ? 'Disconnecting…' : 'Disconnect Google Calendar')}
                      </button>
                    </div>
                    {meetingProvider === 'google' && !googleWriteReady && (
                      <div className="banner warning" style={{ marginBottom: 12 }}>
                        Select a Google calendar in Calendar Sync before creating Meet links.{' '}
                        <Link to={`/calendar?stack=${currentProfession}`}>Open Calendar Sync</Link>
                      </div>
                    )}
                    <div className="list" style={{ marginBottom: 12 }}>
                      <div>
                        <div className="field-label">Meeting time ({meetingTimeZoneLabel})</div>
                        {hasBookingSlot ? (
                          <>
                            <div className="hint" style={{ marginTop: 4 }}>
                              {meetingTimeLabel} ({meetingTimeZoneLabel})
                            </div>
                            {!meeting?.joinUrl ? (
                              <label className="inline-gap-2" style={{ marginTop: 8, alignItems: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={meetingTimeConfirmed}
                                  onChange={(e) => setMeetingTimeConfirmed(e.target.checked)}
                                />
                                <span>Confirm this meeting time</span>
                              </label>
                            ) : (
                              <div className="hint" style={{ marginTop: 8 }}>
                                Confirmed for {meetingTimeLabel} ({meetingTimeZoneLabel})
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="inline-gap-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                              <input
                                ref={meetingPickerRef}
                                className="input"
                                type="datetime-local"
                                step={900}
                                value={meetingTimeInput}
                                onChange={(e) => {
                                  if (!meetingTimeDirty) {
                                    setMeetingTimeInputZone(meetingTimeZoneLabel)
                                  }
                                  setMeetingTimeInput(e.target.value)
                                  setMeetingTimeDirty(true)
                                  setMeetingError(null)
                                }}
                                disabled={Boolean(meeting?.joinUrl)}
                              />
                            </div>
                            <div className="hint" style={{ marginTop: 8 }}>
                              Required to send the link.
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {meetingNotice && (
                      <div className="banner success" style={{ marginBottom: 12 }}>
                        {meetingNotice}
                      </div>
                    )}
                    {meetingError && (
                      <div className="banner error" style={{ marginBottom: 12 }}>
                        {meetingError}
                      </div>
                    )}
                    {meeting?.joinUrl ? (
                      <div className="list">
                        <div>
                          <div className="field-label">Client join link</div>
                          <div className="inline-gap-2" style={{ flexWrap: 'wrap' }}>
                            <input className="input" readOnly value={meeting.joinUrl} />
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={() => handleCopyMeetingLink(meeting.joinUrl, 'Client link')}
                            >
                              Copy
                            </button>
                            <a className="btn btn-outline" href={meeting.joinUrl} target="_blank" rel="noreferrer">
                              Open
                            </a>
                          </div>
                        </div>
                        {meeting.hostUrl && (
                          <div>
                            <div className="field-label">Host link</div>
                            <div className="inline-gap-2" style={{ flexWrap: 'wrap' }}>
                              <input className="input" readOnly value={meeting.hostUrl} />
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => handleCopyMeetingLink(meeting.hostUrl, 'Host link')}
                              >
                                Copy
                              </button>
                              <a className="btn btn-outline" href={meeting.hostUrl} target="_blank" rel="noreferrer">
                                Open
                              </a>
                            </div>
                          </div>
                        )}
                        <div className="inline-gap-2 hint" style={{ flexWrap: 'wrap' }}>
                          <span>Created: {fmtMeetingStamp(meeting.createdAt)}</span>
                          <span>Sent: {fmtMeetingStamp(meeting.sentAt)}</span>
                        </div>
                        <div className="inline-gap-2" style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={handleResendMeeting}
                            disabled={meetingBusy}
                          >
                            {meetingBusy ? 'Sending…' : 'Resend to client'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="inline-gap-2" style={{ flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleCreateMeeting}
                          disabled={meetingBusy || (meetingProvider === 'google' && !googleWriteReady)}
                        >
                          {meetingBusy ? 'Creating…' : 'Create meeting link'}
                        </button>
                        <span className="hint">We’ll email the link to the client.</span>
                      </div>
                    )}
                    {meetingProvider === 'zoom' && (
                      <div className="inline-gap-2" style={{ marginTop: 16, alignItems: 'center' }}>
                        <label className="inline-gap-2" style={{ alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={zoomAutoCreate}
                            onChange={(e) => handleToggleZoomAutoCreate(e.target.checked)}
                            disabled={zoomSaving || zoomDisconnecting}
                          />
                          <span>Auto-create Zoom links for future confirmed bookings</span>
                        </label>
                        {zoomSaving && <span className="hint">Saving…</span>}
                      </div>
                    )}
                    {meetingProvider === 'google' && (
                      <div className="inline-gap-2" style={{ marginTop: 16, alignItems: 'center' }}>
                        <label className="inline-gap-2" style={{ alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={googleAutoCreate}
                            onChange={(e) => handleToggleGoogleAutoCreate(e.target.checked)}
                            disabled={googleSaving || googleDisconnecting || !googleWriteReady}
                          />
                          <span>Auto-create Google Meet links for future confirmed bookings</span>
                        </label>
                        {googleSaving && <span className="hint">Saving…</span>}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {!summaryOnly && (
        <section className="section">
          <div className="section-title">Next steps</div>
          <div className="card">
            <div className="list">
              <div className="inline-gap-2" style={{ marginTop: 12, alignItems: 'flex-start' }}>
                {data.status !== 'PENDING' ? (
                  <div className="banner info" style={{ margin: 0, width: '100%' }}>
                    {data.status === 'ACCEPTED' || data.status === 'CONFIRMED'
                      ? 'Request accepted. Open the in-app chat box to continue with the client.'
                      : data.status === 'COMPLETED'
                        ? 'This job is completed.'
                        : 'This request is closed.'}
                  </div>
                ) : (
                  <>
                    <button className="btn" onClick={decline} disabled={busy || cardBusy}>Decline</button>
                    <div style={{ flex:1 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                      {slotElapsed && (
                        <div className="banner warning" style={{ margin: 0 }}>
                          Requested time has passed; you can still accept and arrange details in the in-app chat box.
                        </div>
                      )}
                      <button className="btn btn-primary" onClick={accept} disabled={busy}>
                        {acceptancePerk.label ? 'Accept request' : 'Confirm & accept'}
                      </button>
                    </div>
                  </>
                )}
                {(data.status === 'ACCEPTED' || data.status === 'CONFIRMED') && (
                  <button className="btn btn-primary" onClick={markCompleted} disabled={busy || cardBusy}>
                    Mark completed
                  </button>
                )}
              </div>
            </div>
            {(data.reviewReceived?.submitted || data.status === 'ACCEPTED') && (
              <>
                {data.reviewReceived?.submitted ? (
                  <div id="review" className="appointment-review-card" style={{ marginTop: 16 }} ref={reviewSectionRef}>
                    <div className="detail-label">Client review</div>
                    <div className="hint" style={{ marginBottom: 4 }}>
                      {data.reviewReceived.reviewerFirstName || 'Client'}
                    </div>
                    <RatingStarsDisplay value={data.reviewReceived.rating || 0} showValue />
                    {data.reviewReceived.comment ? (
                      <p className="hint" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                        “{data.reviewReceived.comment}”
                      </p>
                    ) : (
                      <p className="hint" style={{ marginTop: 8 }}>
                        No written feedback.
                      </p>
                    )}
                    <p className="hint" style={{ marginTop: 8 }}>
                      Received {formatReviewDate(data.reviewReceived.createdAt)}
                    </p>
                    <div className="appointment-review-reply">
                      <div className="detail-label">
                        {providerReply?.comment ? 'Update your reply' : 'Reply to this review'}
                      </div>
                      <p className="hint" style={{ marginTop: 4 }}>
                        Your reply is shown on your public EUCA Match profile.
                      </p>
                      <textarea
                        ref={reviewReplyInputRef}
                        className="input"
                        placeholder="Write a short professional reply"
                        rows={3}
                        style={{ marginTop: 10 }}
                        value={providerReplyDraft}
                        onChange={(e) => {
                          setProviderReplyDraft(e.target.value)
                          setReplyError(null)
                          setReplyNotice(null)
                        }}
                        disabled={replyBusy}
                      />
                      {providerReply?.comment ? (
                        <p className="hint" style={{ marginTop: 8 }}>
                          Last reply sent {formatReviewDate(providerReply.updatedAt || providerReply.createdAt)}
                        </p>
                      ) : null}
                      {replyError && (
                        <div className="field-help" role="alert" style={{ marginTop: 8 }}>
                          {replyError}
                        </div>
                      )}
                      {replyNotice && (
                        <div className="hint" style={{ marginTop: 8, color: 'var(--success)' }}>
                          {replyNotice}
                        </div>
                      )}
                      <div className="inline-gap-2" style={{ marginTop: 12 }}>
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={handleSubmitProviderReply}
                          disabled={replyBusy}
                        >
                          {replyBusy ? 'Saving…' : providerReply?.comment ? 'Update reply' : 'Post reply'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {data.status === 'ACCEPTED' ? (
                  <div className="appointment-review-card" style={{ marginTop: 16 }}>
                    <div className="detail-label">Rate this client</div>
                    <p className="hint" style={{ marginBottom: 8 }}>
                      Please rate the client after the booking is completed.
                    </p>
                    {data.review?.client?.submitted ? (
                      <div className="hint" style={{ color: 'var(--success)' }}>
                        Thanks — your rating was saved.
                      </div>
                    ) : reviewReady ? (
                      <>
                        <RatingStarsInput
                          value={clientReviewDraft.rating}
                          onChange={(val) => {
                            setClientReviewDraft({ rating: val })
                            setReviewError(null)
                            setReviewNotice(null)
                          }}
                          disabled={reviewBusy}
                        />
                        {reviewError && (
                          <div className="field-help" role="alert" style={{ marginTop: 8 }}>
                            {reviewError}
                          </div>
                        )}
                        {reviewNotice && (
                          <div className="hint" style={{ marginTop: 8, color: 'var(--success)' }}>
                            {reviewNotice}
                          </div>
                        )}
                        <div className="inline-gap-2" style={{ marginTop: 12 }}>
                          <button
                            className="btn btn-primary"
                            type="button"
                            onClick={handleSubmitClientReview}
                            disabled={reviewBusy}
                          >
                            {reviewBusy ? 'Submitting…' : 'Submit rating'}
                          </button>
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </section>
      )}

      {showPaymentSection && (
        <section className="section" ref={paymentSectionRef}>
          <div className="section-title">{paymentOnly ? 'Invoice details' : 'Payment'}</div>
          <div className="card">
            {stripeStatusLoading && <Spinner label="Checking Stripe status…" />}
            {stripeStatusError && (
              <div className="banner error" style={{ marginBottom: 12 }}>
                {stripeStatusError}
              </div>
            )}
            {!stripeStatusLoading && (
              <>
                {!isConfirmed && (
                  <div className="banner warning" style={{ marginBottom: 12 }}>
                    Invoices are available for confirmed bookings only.
                  </div>
                )}
                {!stripeStatus?.connected && (
                  <div className="banner info" style={{ marginBottom: 12 }}>
                    Connect Stripe to send invoices and receive payouts.
                  </div>
                )}
                {stripeNeedsStandard ? (
                  <div className="banner warning" style={{ marginBottom: 12 }}>
                    Your Stripe account is set up as Express. Please reconnect to Stripe to use Standard payouts.
                  </div>
                ) : stripeNeedsReconnect ? (
                  <div className="banner warning" style={{ marginBottom: 12 }}>
                    Please reconnect Stripe to enable invoicing.
                  </div>
                ) : stripeNeedsSetup ? (
                  <div className="banner warning" style={{ marginBottom: 12 }}>
                    Stripe needs more information before you can send invoices.
                  </div>
                ) : null}
                {stripeStatus?.connected && (
                  <div className="meeting-connection-row">
                    <div className="hint">
                      Stripe connected.
                    </div>
                    <button
                      type="button"
                      className="btn-text meeting-disconnect-btn"
                      onClick={handleDisconnectStripe}
                      disabled={stripeDisconnecting}
                    >
                      {stripeDisconnecting ? 'Disconnecting…' : 'Disconnect Stripe'}
                    </button>
                  </div>
                )}
                {!stripeReady ? (
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleConnectStripe}
                    disabled={stripeDisconnecting}
                  >
                    {stripeNeedsStandard
                      ? 'Reconnect Stripe'
                      : stripeNeedsReconnect
                        ? 'Reconnect Stripe'
                        : stripeNeedsSetup
                          ? 'Finish Stripe setup'
                          : 'Connect Stripe'}
                  </button>
                ) : (
                  <>
                    {isConfirmed && (
                      <>
                        {invoiceNotice && (
                          <div
                            className={`banner ${invoiceNotice.type === 'success' ? 'success' : 'error'}`}
                            style={{ width: '100%', margin: '0 0 12px' }}
                            role="status"
                            aria-live="polite"
                          >
                            {invoiceNotice.message}
                          </div>
                        )}
                        <div className="list">
                          <div>
                            <div className="field-label">Amount ({invoiceCurrencyCode})</div>
                            <input
                              className="input"
                              type="number"
                              min="0"
                              step="0.01"
                              value={invoiceDraft.amount}
                              onChange={(e) =>
                                setInvoiceDraft((prev) => ({ ...prev, amount: e.target.value }))
                              }
                              placeholder="e.g. 120"
                            />
                          </div>
                          <div className="fee-estimate" aria-live="polite">
                            <div className="fee-estimate-row">
                              <span className="fee-estimate-label">Est. net payout</span>
                              <span className="fee-estimate-value">
                                {netDisplayCents != null ? fmtFeeAmount(netDisplayCents / 100) : '—'}
                              </span>
                            </div>
                            <details className="fee-breakdown">
                              <summary>Show fee breakdown</summary>
                              <div className="fee-breakdown-body">
                                {passStripeFee && (
                                  <div className="fee-breakdown-row">
                                    <span>Client pays (gross)</span>
                                    <span>{grossCents != null ? fmtFeeAmount(grossCents / 100) : '—'}</span>
                                  </div>
                                )}
                                <div className="fee-breakdown-row">
                                  <span>{passStripeFee ? 'Estimated Stripe fee (passed to client)' : 'Estimated Stripe fee'}</span>
                                  <span>{feeCents != null ? fmtFeeAmount(feeCents / 100) : '—'}</span>
                                </div>
                                {passStripeFee && (
                                  <div className="fee-breakdown-row">
                                    <span>Card processing fee (line item)</span>
                                    <span>{surchargeCents != null ? fmtFeeAmount(surchargeCents / 100) : '—'}</span>
                                  </div>
                                )}
                                <div className="fee-breakdown-row">
                                  <span>{invoiceTaxInclusive ? 'Stripe processing fees (ex tax)' : 'Stripe processing fees'}</span>
                                  <span>{feeProcessingCents != null ? fmtFeeAmount(feeProcessingCents / 100) : '—'}</span>
                                </div>
                                {feeTaxCents != null && (
                                  <div className="fee-breakdown-row">
                                    <span>{invoiceTaxInclusive ? `${invoiceTaxLabel} component` : `${invoiceTaxLabel} (added on top)`}</span>
                                    <span>{fmtFeeAmount(feeTaxCents / 100)}</span>
                                  </div>
                                )}
                                <div className="fee-breakdown-note">Formula: {stripeFeeRule.label}</div>
                                <div className="fee-breakdown-note">Final Stripe fees vary by card type and region.</div>
                              </div>
                            </details>
                          </div>
                          <div>
                            <label className="inline-gap-2" style={{ alignItems: 'center', marginTop: 4 }}>
                              <input
                                type="checkbox"
                                checked={!!invoiceDraft.passStripeFee}
                                onChange={(e) =>
                                  setInvoiceDraft((prev) => ({ ...prev, passStripeFee: e.target.checked }))
                                }
                              />
                              <span>Pass card processing fee to client</span>
                            </label>
                            {invoiceDraft.passStripeFee && (
                              <div className="hint" style={{ marginTop: 6 }}>
                                The invoice total will be increased so you receive the full amount.
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="inline-gap-2" style={{ alignItems: 'center', marginTop: 4 }}>
                              <input
                                type="checkbox"
                                checked={!!invoiceDraft.includeGst}
                                disabled={!invoiceSupportsTax}
                                onChange={(e) =>
                                  setInvoiceDraft((prev) => ({
                                    ...prev,
                                    includeGst: invoiceSupportsTax ? e.target.checked : false,
                                  }))
                                }
                              />
                              <span>
                                {invoiceSupportsTax
                                  ? invoiceTaxInclusive
                                    ? `Include ${invoiceTaxLabel} (${invoiceTaxPercent}%) — amount is tax-inclusive`
                                    : `Include ${invoiceTaxLabel} (${invoiceTaxPercent}%) — tax is added on top`
                                  : invoiceCountryCode
                                    ? `${invoiceTaxLabel} is unavailable for ${invoiceCountryLabel} providers`
                                    : `${invoiceTaxLabel} is unavailable until provider country is confirmed`}
                              </span>
                            </label>
                            {!invoiceSupportsTax && (
                              <div className="hint" style={{ marginTop: 6 }}>
                                Tax calculation is unavailable for this provider country/region.
                              </div>
                            )}
                          </div>
                          {invoiceTaxRequiresAbn && invoiceDraft.includeGst && (
                            <>
                              <div>
                                <div className="field-label">ABN (required for Australian tax invoices)</div>
                                <input
                                  className={`input${abnInvalid ? ' error' : ''}`}
                                  value={invoiceDraft.abn}
                                  onChange={(e) =>
                                    setInvoiceDraft((prev) => ({ ...prev, abn: e.target.value }))
                                  }
                                  placeholder="11 digit ABN"
                                  inputMode="numeric"
                                />
                                {abnInvalid && (
                                  <div className="field-help">Enter a valid ABN.</div>
                                )}
                                {!abnInvalid && abnRequired && (
                                  <div className="hint" style={{ marginTop: 6 }}>
                                    Required when including tax for Australian invoices.
                                  </div>
                                )}
                              </div>
                              <label className="inline-gap-2" style={{ alignItems: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={invoiceDraft.saveAbn !== false}
                                  onChange={(e) =>
                                    setInvoiceDraft((prev) => ({ ...prev, saveAbn: e.target.checked }))
                                  }
                                />
                                <span>Save ABN for future invoices</span>
                              </label>
                            </>
                          )}
                          <div>
                            <div className="field-label">Description</div>
                            <input
                              className="input"
                              value={invoiceDraft.description}
                              onChange={(e) =>
                                setInvoiceDraft((prev) => ({ ...prev, description: e.target.value }))
                              }
                              placeholder="Booking details"
                            />
                          </div>
                          <div>
                            <div className="field-label">Note to client (optional)</div>
                            <textarea
                              className="input"
                              rows={3}
                              value={invoiceDraft.memo}
                              onChange={(e) =>
                                setInvoiceDraft((prev) => ({ ...prev, memo: e.target.value }))
                              }
                              placeholder="Add any payment notes or instructions"
                            />
                          </div>
                          {invoiceError && (
                            <div className="banner error" style={{ margin: 0 }}>
                              {invoiceError}
                            </div>
                          )}
                          <div className="inline-gap-2" style={{ marginTop: 8 }}>
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={handleSendInvoice}
                              disabled={invoiceBusy}
                            >
                              {invoiceBusy ? 'Sending…' : invoiceSentRecently ? 'Sent ✓' : 'Send invoice'}
                            </button>
                            <button
                              className="btn"
                              type="button"
                              onClick={loadInvoices}
                              disabled={invoiceLoading}
                            >
                              Refresh
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          <div className="card" style={{ marginTop: 12 }}>
            <div className="detail-label">Invoice history</div>
            {invoiceLoading ? (
              <Spinner label="Loading invoices…" />
            ) : sortedInvoices.length === 0 ? (
              <div className="hint">No invoices yet.</div>
            ) : (
              <div className="list invoice-list">
                {sortedInvoices.map((inv) => (
                  <div key={inv.invoiceId} className="list-row invoice-row" style={{ alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{fmtInvoiceAmount(inv.amountCents, inv.currency)}</div>
                      <div className="inline-gap-2" style={{ flexWrap: 'wrap', marginTop: 4 }}>
                        {renderInvoiceStatus(inv.status)}
                        <span className="hint">
                          {inv.sentAt
                            ? `Sent ${new Date(inv.sentAt).toLocaleString()}`
                            : inv.createdAt
                              ? `Created ${new Date(inv.createdAt).toLocaleString()}`
                              : ''}
                        </span>
                        {inv.paidAt ? (
                          <span className="hint">Paid {new Date(inv.paidAt).toLocaleString()}</span>
                        ) : null}
                      </div>
                      {inv.description && <div className="minor">{inv.description}</div>}
                    </div>
                    <div className="inline-gap-2" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {inv.hostedInvoiceUrl && (
                        <a className="btn btn-outline" href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                          View
                        </a>
                      )}
                      {['paid', 'void', 'voided'].includes(String(inv.status || '').toLowerCase()) ? null : (
                        <button className="btn" type="button" onClick={() => handleResendInvoice(inv.invoiceId)}>
                          Resend
                        </button>
                      )}
                      {['paid', 'void', 'voided'].includes(String(inv.status || '').toLowerCase()) ? null : (
                        <button className="btn btn-outline" type="button" onClick={() => handleVoidInvoice(inv.invoiceId)}>
                          Void
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      {needCard && (
        <div className="overlay show inbox-detail-overlay" onClick={() => !cardBusy && setNeedCard(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="sheet-header">
              <div className="sheet-title">Confirm & pay</div>
              <button
                className="btn close"
                onClick={() => { if (!cardBusy) { setNeedCard(false); setCardErr(null) } }}
                disabled={cardBusy}
              >
                ✕
              </button>
            </div>
            <div className="sheet-body">
              {cardErr && <div className="banner error" role="alert">{cardErr}</div>}
              <PaymentCard
                amount={fmtInvoiceAmount(0, invoiceCurrencyCode)}
                blurb="Confirm your acceptance and continue via the in-app chat box."
                summaryItems={[
                  { label: 'Acceptance fee', value: `Secure payment · ${fmtInvoiceAmount(0, invoiceCurrencyCode)}` },
                  { label: 'Unlocks', value: 'Client contact & payout summary' },
                ]}
                note="Processed securely by Stripe. Fee is charged once per confirmed booking."
              >
                <div>
                  <div className="field-label">Name on card (optional)</div>
                  <input
                    className="input"
                    placeholder="Exact name on card"
                    value={cardName}
                    onChange={e => setCardName(e.target.value)}
                  />
                </div>
                <div>
                  <div className="field-label">Card details</div>
                  <div className="stripe-input">
                    <CardElement options={{ hidePostalCode: true }} />
                  </div>
                </div>
              </PaymentCard>
            </div>
            <div className="sheet-footer">
              <button className="btn" onClick={() => { if (!cardBusy) { setNeedCard(false); setCardErr(null) } }} disabled={cardBusy}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={submitCardAndAccept}
                disabled={cardBusy || !stripe || !elements}
              >
                {cardBusy ? 'Processing…' : 'Confirm & accept'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function InboxDetail() {
  const { selectionId = '' } = useParams<{ selectionId: string }>()
  const [searchParams] = useSearchParams()
  const stackParam = searchParams.get('stack')
  const preferredStack = stackParam ? resolveProfessionParam(stackParam) : null
  return <InboxDetailContent selectionId={selectionId} initialStack={preferredStack} />
}
